import React, { FC, useEffect, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import { GetVehiclesQuery, VehicleFilterInput } from "../../gql/graphql";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import ShareParkTable, {
  usePaginationAndFilters,
} from "../../components/table/ShareParkTable";

export const VEHICLES = gql`
  query GetVehicles(
    $filter: VehicleFilterInput
    $take: Int
    $skip: Int
    $orderBy: [VehicleSortInput!]
  ) {
    vehicles(where: $filter, take: $take, skip: $skip, order: $orderBy) {
      totalCount
      items {
        id
        parkerId
        parker {
          id
          email
          firstName
          lastName
        }
        plate
        make
        model
        colour
        state
        isActive
        isElectric
      }
    }
  }
`;

type Vehicle = NonNullable<
  NonNullable<NonNullable<GetVehiclesQuery["vehicles"]>["items"]>[number]
>;
const columnHelper = createColumnHelper<Vehicle>();

// Renders a muted em dash for null/empty/whitespace string cells so legacy
// records with blank fields still read clearly in the table.
const renderEmptyable = (value: unknown) => {
  const str = value == null ? "" : String(value);
  return str.trim() !== "" ? str : <span className="text-muted">—</span>;
};

const columns = [
  columnHelper.accessor("plate", {
    header: "Plate",
    cell: (info) => {
      // Treat null, empty and whitespace-only plates as "no plate" so the row
      // still shows a visible, clickable link to edit/delete the record.
      const plate = (info.getValue() as string | null) ?? "";
      const hasPlate = plate.trim() !== "";
      return (
        <Link to={`/vehicles/${info.row.original?.id}`}>
          {hasPlate ? (
            plate
          ) : (
            <span className="text-muted fst-italic">(no plate)</span>
          )}
        </Link>
      );
    },
  }),
  columnHelper.accessor("parker.email", {
    header: "Email",
    cell: (info) => {
      return (
        <Link to={"/parkers/" + info.row.original.parkerId}>
          {info.renderValue()}
        </Link>
      );
    },
  }),
  columnHelper.accessor("make", {
    header: "Make",
    cell: (info) => renderEmptyable(info.getValue()),
  }),
  columnHelper.accessor("model", {
    header: "Model",
    cell: (info) => renderEmptyable(info.getValue()),
  }),
  columnHelper.accessor("colour", {
    header: "Colour",
    cell: (info) => renderEmptyable(info.getValue()),
  }),
  columnHelper.accessor("state", {
    header: "State",
    cell: (info) => renderEmptyable(info.getValue()),
  }),
];

const VehicleHeaders = [
  { label: "ID", key: "id" },
  { label: "Email", key: "email" },
  { label: "First Name", key: "firstName" },
  { label: "Last Name", key: "lastName" },
  { label: "Plate", key: "plate" },
  { label: "Make", key: "make" },
  { label: "Model", key: "model" },
  { label: "Colour", key: "colour" },
  { label: "State", key: "state" },
  { label: "Is Active", key: "isActive" },
];

type VehicleType = NonNullable<
  NonNullable<NonNullable<GetVehiclesQuery["vehicles"]>["items"]>[number]
>;

export const Vehicles: FC<{ parkerId?: string }> = ({ parkerId }) => {
  const { parkerId: id } = useParams();
  if (!parkerId) parkerId = id;

  const { pagination, setPagination, columnFilters, setColumnFilters } =
    usePaginationAndFilters();
  const [showInactive, setShowInactive] = useState(false);

  // Add sorting state
  const [sorting, setSorting] = useState([
    {
      id: "plate", // Default sorting column
      desc: false, // Default sorting direction (ascending)
    },
  ]);

  const where = React.useMemo(() => {
    const where: VehicleFilterInput = {};

    if (parkerId) {
      where.parkerId = { eq: parkerId };
    }

    if (!showInactive) {
      where.isActive = { eq: true };
    }
    for (const filter of columnFilters) {
      switch (filter.id) {
        case "parker_email":
          if (filter.value != null) {
            where.parker = { email: { contains: String(filter.value) } };
          }
          break;
        case "plate":
        case "make":
        case "model":
        case "colour":
        case "state":
          if (filter.value != null) {
            where[filter.id] = { contains: String(filter.value) };
          }
          break;
      }
    }
    return where;
  }, [showInactive, columnFilters, parkerId]);

  const orderBy = React.useMemo(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      return {
        [sort.id]: sort.desc ? "DESC" : "ASC",
      };
    }
    return undefined;
  }, [sorting]);

  const { data, loading } = useQuery<GetVehiclesQuery>(VEHICLES, {
    variables: {
      take: pagination.pageSize,
      skip: pagination.pageSize * pagination.pageIndex,
      filter: where,
      orderBy: orderBy, // Pass sorting to the query
    },
    fetchPolicy: "network-only",
  });
  const [flat, setFlat] = useState<
    (Omit<VehicleType, "parker"> &
      Pick<NonNullable<VehicleType["parker"]>, "firstName" | "lastName">)[]
  >([]);

  useEffect(() => {
    if (data && data.vehicles) {
      let flat = data?.vehicles?.items
        ?.filter((x) => x?.isActive || showInactive)
        .map((vehicle) => {
          return {
            id: vehicle?.id,
            email: vehicle?.parker?.email,
            firstName: vehicle?.parker?.firstName,
            lastName: vehicle?.parker?.lastName,
            plate: vehicle?.plate,
            make: vehicle?.make,
            model: vehicle?.model,
            colour: vehicle?.colour,
            state: vehicle?.state,
            isActive: vehicle?.isActive ?? false,
            isElectric: vehicle?.isElectric ?? false,
          };
        });
      if (flat) {
        setFlat(flat);
      }
    }
  }, [data, showInactive]);

  const parkerUrl = parkerId ? `/parkers/${parkerId}` : "";

  return (
    <div>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">{!parkerId && <span>Vehicles</span>}</h1>

        <div className="btn-toolbar mb-2 mb-md-0">
          <div className="btn-group me-2">
            {!parkerId && (
              <CSVLink
                className="btn btn-sm btn-outline-secondary"
                data={flat as object[]}
                headers={VehicleHeaders}
                filename={"vehicles.csv"}
              >
                Export
              </CSVLink>
            )}
            {
              <span
                className={
                  showInactive
                    ? "btn btn-sm btn-secondary"
                    : "btn btn-sm btn-outline-secondary"
                }
                onClick={() => setShowInactive(!showInactive)}
              >
                Inactive
              </span>
            }
          </div>

          <Link
            className="btn btn-sm btn-primary ml-2"
            to={parkerUrl + "/vehicles/new"}
          >
            Add New
          </Link>
        </div>
      </div>

      <ShareParkTable
        data={data?.vehicles ?? { items: [], totalCount: 0 }}
        columns={columns as ColumnDef<Vehicle>[]}
        pagination={pagination}
        setPagination={setPagination}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        sorting={sorting} // Pass sorting state
        setSorting={setSorting} // Pass sorting setter
        loading={loading}
        isActiveKey="isActive"
      />
    </div>
  );
};
